import React from 'react'
import { DropdownMenu } from '@/shared/ui/DropdownMenu'
import { Tabs } from '@/shared/ui'
import { MoreHorizontal, CheckCircle, AlertTriangle, AlertCircle, MessageCircle } from 'lucide-react'
import type { OpportunityItem } from '@/modules/oportunidades/types'
import { listAuditedOpportunitiesByStatus, markOpportunityAuditResolved } from '../services/auditoriaService'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { isMasterByEmail } from '@/shared/utils/role'
import AuditReportsModal from '../components/AuditReportsModal'
import { OportunidadeModalProvider, useOportunidadeModal } from '@/modules/oportunidades/context/OportunidadeModalContext'
import { AUDITORIA_TITLE, AUDITORIA_DESC, PER_PAGE_LABEL, LOADING_TEXT, NO_ITEMS_TEXT } from '../utils/strings'

export default function AuditoriaPage() {
  const [items, setItems] = React.useState<OpportunityItem[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [page, setPage] = React.useState(1)
  const [pageSize, setPageSize] = React.useState(25)
  const [totalPages, setTotalPages] = React.useState(1)
  const [tab, setTab] = React.useState<'pending'|'resolved'>('pending')

  const [reportsOpen, setReportsOpen] = React.useState(false)
  const [current, setCurrent] = React.useState<OpportunityItem | null>(null)

  const load = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { items, totalPages } = await listAuditedOpportunitiesByStatus(tab, page, pageSize)
      setItems(items)
      setTotalPages(totalPages)
    } catch (e: any) {
      setError(e?.message || String(e))
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, tab])

  React.useEffect(() => { load() }, [load])

  async function onResolve(it: OpportunityItem){
    setError(null)
    try {
      await markOpportunityAuditResolved(it.id)
      await load()
    } catch (e:any) {
      setError(e?.message || String(e))
    }
  }

  return (
    <OportunidadeModalProvider>
      <AuditoriaInner
        items={items}
        loading={loading}
        error={error}
        tab={tab}
        setTab={setTab}
        pageSize={pageSize}
        setPageSize={setPageSize}
        page={page}
        setPage={setPage}
        totalPages={totalPages}
        onOpenReports={(it)=>{ setCurrent(it); setReportsOpen(true) }}
        onResolve={onResolve}
        reportsOpen={reportsOpen}
        current={current}
        setReportsOpen={setReportsOpen}
      />
    </OportunidadeModalProvider>
  )
}

function AuditoriaInner(props: {
  items: OpportunityItem[]
  loading: boolean
  error: string | null
  tab: 'pending'|'resolved'
  setTab: (t: 'pending'|'resolved')=>void
  pageSize: number
  setPageSize: (n:number)=>void
  page: number
  setPage: (n:number)=>void
  totalPages: number
  onOpenReports: (it: OpportunityItem)=>void
  onResolve: (it: OpportunityItem)=>void
  reportsOpen: boolean
  current: OpportunityItem | null
  setReportsOpen: (v: boolean)=>void
}){
  const { openModal } = useOportunidadeModal()
  const { items, loading, error, tab, setTab, pageSize, setPageSize, page, setPage, totalPages, onOpenReports, onResolve, reportsOpen, current, setReportsOpen } = props
  return (
    <div className="w-full">
      <div className="mb-4">
        <div className="text-2xl font-semibold">{AUDITORIA_TITLE}</div>
        <div className="text-xs text-foreground/70">{AUDITORIA_DESC}</div>
      </div>

      <Tabs tabs={[{ value: 'pending', label: 'Pendente' }, { value: 'resolved', label: 'Resolvidas' }]} value={tab} onValueChange={(v)=>{ setTab(v as any); setPage(1) }} />

      {error && (
        <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>
      )}

      {loading ? (
        <div className="text-sm text-foreground/70">{LOADING_TEXT}</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-foreground/70">{NO_ITEMS_TEXT}</div>
      ) : (
        <div className="flex flex-col gap-3">
          {items.map((it) => (
            <React.Fragment key={it.id}>
              <div className="lg:hidden">
                <OpportunityCardCompact
                  item={it}
                  onBug={()=> onOpenReports(it)}
                  onGraph={()=> openModal({ id: it.id, symbol: it.symbol, name: it.name, logo_url: it.logo_url, kind: 'opportunity' })}
                  onResolve={()=> onResolve(it)}
                  showResolve={tab==='pending'}
                />
              </div>
              <div className="hidden lg:block">
                <OpportunityCard
                  item={it}
                  onBug={()=> onOpenReports(it)}
                  onGraph={()=> openModal({ id: it.id, symbol: it.symbol, name: it.name, logo_url: it.logo_url, kind: 'opportunity' })}
                  onResolve={()=> onResolve(it)}
                  showResolve={tab==='pending'}
                />
              </div>
            </React.Fragment>
          ))}
        </div>
      )}

      <div className="mt-4 flex items-center justify-end gap-4">
        <Pagination page={page} totalPages={totalPages} onPage={setPage} />
        <div className="flex items-center gap-2">
          <label className="text-sm">{PER_PAGE_LABEL}</label>
          <select value={pageSize} onChange={e=>{ setPageSize(Number(e.target.value)); setPage(1) }} className="rounded border bg-background px-2 py-1 text-sm">
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      <AuditReportsModal open={reportsOpen} onOpenChange={setReportsOpen} item={current} />
    </div>
  )
}

function OpportunityCardCompact({ item, onBug, onGraph, onResolve, showResolve }: { item: OpportunityItem; onBug: ()=>void; onGraph: ()=>void; onResolve: ()=>void; showResolve: boolean }) {
  const { user, profile } = useAuth()
  const isMaster = isMasterByEmail(user?.email) || profile?.role === 'master'
  return (
    <div className="rounded-xl border border-[#161616] bg-background/40 p-3 min-h-[110px] md:min-h-[110px]">
      <div className="grid grid-cols-[auto,1fr,auto] grid-rows-[auto,auto,auto] items-center gap-x-3 gap-y-2">
        <div className="row-span-3 col-start-1 flex items-center justify-start">
          <div className="inline-flex items-center rounded p-1">
            {item.logo_url ? (
              <img src={item.logo_url} alt={item.symbol} className="h-[2.65rem] w-[2.65rem] flex-shrink-0 rounded-full object-cover" />
            ) : (
              <div className="h-[2.65rem] w-[2.65rem] flex-shrink-0 rounded-full bg-muted" />
            )}
          </div>
        </div>
        <div className="col-start-2 flex items-center gap-2 text-xs">
          <span className={["font-medium", item.objective==="Long" ? "text-emerald-400" : item.objective==="Short" ? "text-red-400" : "text-foreground/80"].join(" ")}>{item.objective || "-"}</span>
          {item.nearBreak ? (
            <AlertCircle className="h-4 w-4 text-red-500" />
          ) : item.uiWarning ? (
            <AlertTriangle className="h-4 w-4 text-amber-400" />
          ) : String(item.status || "").toLowerCase() === "active" ? (
            <CheckCircle className="h-4 w-4 text-emerald-400" />
          ) : (
            <AlertTriangle className="h-4 w-4 text-amber-400" />
          )}
          <span className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-[11px] text-foreground/80">
            <MessageCircle className="h-3.5 w-3.5" />
            {(item as any).auditCount ?? 0}
          </span>
        </div>
      </div>
    </div>
  )
}

function OpportunityCard({ item, onBug, onGraph, onResolve, showResolve }: { item: OpportunityItem; onBug: ()=>void; onGraph: ()=>void; onResolve: ()=>void; showResolve: boolean }) {
  const { user, profile } = useAuth()
  const isMaster = isMasterByEmail(user?.email) || profile?.role === 'master'
  return (
    <div className="rounded-xl border border-[#161616] bg-background/40 p-4">
      <div className="grid items-center gap-6 md:grid-cols-[1.2fr,0.9fr,1fr,0.8fr,0.6fr,0.4fr]">
        <div className="flex items-center gap-3">
          {item.logo_url ? (
            <img src={item.logo_url} alt={item.symbol} className="h-8 w-8 flex-shrink-0 rounded-full object-cover" />
          ) : (
            <div className="h-8 w-8 flex-shrink-0 rounded-full bg-muted" />
          )}
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{item.name || item.symbol}</div>
            <div className="truncate text-xs text-[#434343]">{item.symbol}</div>
          </div>
        </div>
        <div><div className="text-sm font-medium">{[item.fractalLower, item.fractalUpper].filter(Boolean).join(' | ') || '-'}</div></div>
        <div><div className={["text-sm font-medium", item.objective==='Long' ? 'text-emerald-400' : item.objective==='Short' ? 'text-red-400' : 'text-foreground/80'].join(' ')}>{item.objective || '-'}</div></div>
        <div className="flex items-center gap-2">
          {item.nearBreak ? (
            <AlertCircle className="h-5 w-5 text-red-500" />
          ) : item.uiWarning ? (
            <AlertTriangle className="h-5 w-5 text-amber-400" />
          ) : String(item.status || '').toLowerCase() === 'active' ? (
            <CheckCircle className="h-5 w-5 text-emerald-400" />
          ) : (
            <AlertTriangle className="h-5 w-5 text-amber-400" />
          )}
        </div>
        <div className="flex items-center gap-1">
          <MessageCircle className="h-4 w-4 text-foreground/60" />
          <span className="text-xs font-medium text-foreground/80">{(item as any).auditCount ?? 0}</span>
        </div>
        <div className="flex items-center justify-end">
          {isMaster && (
            <DropdownMenu
              trigger={<button type="button" className="inline-flex items-center rounded p-1 hover:bg-muted/30"><MoreHorizontal className="h-5 w-5 text-[#434343]" /></button>}
              items={[{ label: 'Bug Relatado', onSelect: onBug }, { label: 'Ver gráfico', onSelect: onGraph }, ...(showResolve ? [{ label: 'Marcar como Resolvida', onSelect: onResolve }] : [])]}
              align="end"
            />
          )}
        </div>
      </div>
    </div>
  )
}

function Pagination({ page, totalPages, onPage }: { page: number; totalPages: number; onPage: (p:number)=>void }) {
  const [input, setInput] = React.useState('')
  React.useEffect(()=> setInput(String(page)), [page])
  const pages = Array.from({ length: totalPages }).map((_,i)=> i+1).slice(0, 10)
  return (
    <div className="flex flex-wrap items-center gap-2">
      {pages.map(p => (
        <button key={p} onClick={()=> onPage(p)} className={["rounded border px-3 py-1 text-sm", p===page ? 'bg-primary text-primary-foreground' : 'bg-background'].join(' ')}>{p}</button>
      ))}
      <div className="ml-2 flex items-center gap-2">
        <span className="text-sm text-foreground/70">Ir para:</span>
        <input value={input} onChange={e=> setInput(e.target.value)} onKeyDown={e=>{ if(e.key==='Enter'){ const v = Math.min(Math.max(1, Number(input)||1), Math.max(1,totalPages)); onPage(v) } }} className="w-16 rounded border bg-background px-2 py-1 text-sm" />
        <span className="text-xs text-foreground/50">/ {totalPages}</span>
      </div>
    </div>
  )
}

